/**
 * diag.js — opt-in diagnostic logger for the CM post-completion hang
 * investigation. Free in production unless explicitly enabled.
 *
 * Activation: append `?diag=1` to the URL when generating. The flag is
 * read once at module load; no runtime overhead when disabled.
 *
 * Output: console.log with `[CM-DIAG]` prefix + millisecond timestamp,
 * AND pushed to `window.__cmDiag` array for paste-ability if the
 * console is overwhelmed by a hung-page log dump.
 *
 * To capture the trace after a hung generation:
 *   1. Open DevTools → Console (logs appear with [CM-DIAG] prefix)
 *   2. After the page recovers (or force-reload + look at preserved log),
 *      paste `JSON.stringify(window.__cmDiag, null, 2)` into the console
 *      to get a clean stringified trace.
 *
 * Why both: console output may be truncated or lost if the browser
 * itself crashes; the in-memory array survives. Conversely, the array
 * survives across hangs but doesn't survive a page reload — console
 * (with "Preserve log" turned on in DevTools) does.
 */

const DIAG_ENABLED = (() => {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("diag") === "1";
  } catch {
    return false;
  }
})();

// Initialize the in-memory log array on first import. Cleared on each
// fresh page load (?diag=1 navigation) so the trace is scoped to the
// current generation attempt, not accumulated across multiple tries.
const LS_KEY = "__cmDiag";
const LS_CAP = 1000; // hard cap; individual entries are tiny (~120B)

if (DIAG_ENABLED && typeof window !== "undefined") {
  window.__cmDiag = [];
  // Reset localStorage on each fresh page load so prior runs don't pollute.
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

/**
 * Log a diagnostic message. Free no-op when ?diag=1 isn't set.
 *
 * @param {string} label  — short tag identifying the source (e.g.
 *   "Markdown.parseBlocks", "RenderedBrief.render", "BlueprintJobView.poll")
 * @param {object} [data] — optional structured data; small primitives
 *   only (don't pass big strings/objects, they'd bloat the trace)
 */
export function diag(label, data) {
  if (!DIAG_ENABLED) return;
  const t = (typeof performance !== "undefined" ? performance.now() : Date.now()).toFixed(1);
  const entry = data ? { t, label, ...data } : { t, label };
  // eslint-disable-next-line no-console
  console.log("[CM-DIAG]", t, label, data || "");
  try {
    window.__cmDiag.push(entry);
    // Persist to localStorage incrementally. Survives any page hang or
    // force-reload — Russ can recover the trace afterwards via
    // localStorage.getItem('__cmDiag') even if DevTools wasn't open
    // during the hang. Cap the buffer at LS_CAP entries; older entries
    // get dropped from the head when we exceed it (keeps localStorage
    // size bounded even on pathological re-render loops).
    let buf = window.__cmDiag;
    if (buf.length > LS_CAP) {
      buf = buf.slice(buf.length - LS_CAP);
      window.__cmDiag = buf;
    }
    // Writing the whole array on each entry is O(n) but n is small and
    // we'd rather pay write cost than risk losing the trace at the
    // moment of a hang. Synchronous localStorage write is the simplest
    // way to guarantee persistence before the main thread might block.
    localStorage.setItem(LS_KEY, JSON.stringify(buf));
  } catch { /* ignore — private mode, quota exceeded, etc. */ }
}

/**
 * Time a synchronous operation. Logs both start + end with the elapsed
 * milliseconds. Returns whatever the wrapped function returns so it can
 * be used inline.
 *
 * Example:
 *   const blocks = diagTime("Markdown.parseBlocks", () => parseBlocks(source));
 */
export function diagTime(label, fn) {
  if (!DIAG_ENABLED) return fn();
  const start = performance.now();
  diag(`${label}.start`);
  try {
    const result = fn();
    const elapsed = (performance.now() - start).toFixed(1);
    diag(`${label}.end`, { elapsed_ms: parseFloat(elapsed) });
    return result;
  } catch (e) {
    const elapsed = (performance.now() - start).toFixed(1);
    diag(`${label}.error`, { elapsed_ms: parseFloat(elapsed), error: e.message });
    throw e;
  }
}

/** Whether diag is active. Callers can short-circuit expensive labels. */
export function isDiagEnabled() {
  return DIAG_ENABLED;
}

/**
 * Get the current trace as a JSON string. Used by the DiagPanel widget's
 * copy button. Falls back to localStorage if window.__cmDiag is empty
 * (e.g., the page got force-reloaded and we're recovering from disk).
 */
export function getDiagTrace() {
  if (typeof window === "undefined") return "";
  const live = window.__cmDiag || [];
  if (live.length > 0) return JSON.stringify(live, null, 2);
  try {
    const persisted = localStorage.getItem(LS_KEY);
    if (persisted) return persisted;
  } catch { /* ignore */ }
  return "[]";
}

/** Number of entries currently in the live trace. */
export function getDiagCount() {
  if (typeof window === "undefined") return 0;
  return (window.__cmDiag || []).length;
}

/**
 * Copy the diag trace to the system clipboard. Falls back to localStorage
 * if the live array is empty. Returns true on success, false on failure.
 *
 * Uses navigator.clipboard.writeText (modern, async). The DiagPanel
 * widget below calls this from a click handler so we satisfy the
 * "user activation required" rule for clipboard API.
 */
export async function copyDiagTrace() {
  const trace = getDiagTrace();
  try {
    await navigator.clipboard.writeText(trace);
    return true;
  } catch {
    // Fallback: select-all in a hidden textarea + execCommand("copy").
    // execCommand is deprecated but still works in most browsers as a
    // last-ditch path when clipboard API is blocked.
    try {
      const ta = document.createElement("textarea");
      ta.value = trace;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}
